import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, FlaskConical, Plus, Route } from "lucide-react";
import { api, type Hotel, type Location, type LocationType, type QrRoute, type Survey, type SurveyPreviewResolution, type SurveyRule } from "../api/client";

const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const mealPeriods = ["", "breakfast", "lunch", "dinner", "outside_window"];

export function SurveyRulesPage() {
  const queryClient = useQueryClient();
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [previewQrId, setPreviewQrId] = useState("");
  const [previewAt, setPreviewAt] = useState("");
  const [previewResult, setPreviewResult] = useState<SurveyPreviewResolution | null>(null);

  const rules = useQuery({ queryKey: ["survey-rules"], queryFn: () => api<SurveyRule[]>("/api/admin/survey-rules") });
  const surveys = useQuery({ queryKey: ["surveys"], queryFn: () => api<Survey[]>("/api/admin/surveys") });
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels") });
  const locationTypes = useQuery({ queryKey: ["location-types"], queryFn: () => api<LocationType[]>("/api/admin/location-types") });
  const locations = useQuery({ queryKey: ["locations"], queryFn: () => api<Location[]>("/api/admin/locations") });
  const qrRoutes = useQuery({ queryKey: ["qr-routes"], queryFn: () => api<QrRoute[]>("/api/admin/qr-routes") });

  const scopedLocations = useMemo(() => (
    (locations.data ?? []).filter((location) => !selectedHotelId || location.hotelId === selectedHotelId)
  ), [locations.data, selectedHotelId]);

  const createRule = useMutation({
    mutationFn: (body: unknown) => api<SurveyRule>("/api/admin/survey-rules", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["survey-rules"] })
  });

  const updateRuleStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "active" | "paused" | "archived" }) => api<SurveyRule>(`/api/admin/survey-rules/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status })
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["survey-rules"] })
  });

  async function submitRule(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const daysOfWeek = dayLabels
      .map((_, index) => form.get(`day-${index}`) ? index : null)
      .filter((value): value is number => value !== null);
    const start = String(form.get("start") || "");
    const end = String(form.get("end") || "");
    createRule.mutate({
      surveyId: String(form.get("surveyId")),
      name: String(form.get("name")),
      priority: Number(form.get("priority") || 0),
      hotelId: String(form.get("hotelId") || "") || undefined,
      locationTypeId: String(form.get("locationTypeId") || "") || undefined,
      locationId: String(form.get("locationId") || "") || undefined,
      mealPeriod: String(form.get("mealPeriod") || "") || undefined,
      daysOfWeek: daysOfWeek.length ? daysOfWeek : undefined,
      timeWindows: start && end ? [{ start, end }] : undefined,
      conditions: {},
      status: "active"
    });
    event.currentTarget.reset();
    setSelectedHotelId("");
  }

  async function resolvePreview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!previewQrId) return;
    const params = new URLSearchParams({ qrRouteId: previewQrId });
    if (previewAt) params.set("at", new Date(previewAt).toISOString());
    setPreviewResult(await api<SurveyPreviewResolution>(`/api/admin/preview/resolve-survey?${params.toString()}`));
  }

  return (
    <div className="page-stack">
      <section className="tool-hero card rules-hero">
        <div>
          <span className="panel-eyebrow left">Rule Engine</span>
          <h2>Time-based i segment-based ankete za isti QR.</h2>
          <p>Pravila se rjesavaju server-side: prioritet, specificnost, najnovija published verzija i fallback anketa.</p>
        </div>
        <div className="object-brand-card">
          <CalendarClock size={34} />
          <strong>07:00 - 10:30</strong>
          <span>breakfast window, Europe/Podgorica</span>
        </div>
      </section>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Novo pravilo</h2>
            <p>Izaberi anketu, scope i vremenski prozor. Prazna polja znace fallback na siri scope.</p>
          </div>
        </div>
        <form className="inline-form rule-create-form" onSubmit={submitRule}>
          <select name="surveyId" required>
            {(surveys.data ?? []).map((survey) => <option key={survey.id} value={survey.id}>{survey.name}</option>)}
          </select>
          <input name="name" placeholder="Naziv pravila" required />
          <input name="priority" type="number" min="0" max="10000" defaultValue="100" />
          <select name="hotelId" value={selectedHotelId} onChange={(event) => setSelectedHotelId(event.target.value)}>
            <option value="">Svi objekti</option>
            {(hotels.data ?? []).map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
          </select>
          <select name="locationTypeId">
            <option value="">Svi tipovi lokacija</option>
            {(locationTypes.data ?? []).map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
          </select>
          <select name="locationId">
            <option value="">Sve lokacije</option>
            {scopedLocations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </select>
          <select name="mealPeriod">
            {mealPeriods.map((period) => <option key={period || "any"} value={period}>{period || "Bilo koji meal period"}</option>)}
          </select>
          <input name="start" type="time" />
          <input name="end" type="time" />
          <div className="day-picker">
            {dayLabels.map((day, index) => (
              <label key={day}><input name={`day-${index}`} type="checkbox" /> {day}</label>
            ))}
          </div>
          <button type="submit"><Plus size={16} /> Kreiraj pravilo</button>
        </form>
      </section>

      <div className="grid-two">
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Aktivna pravila</h2>
              <p>Redosljed prikaza prati prioritet i zadnju izmjenu.</p>
            </div>
          </div>
          <div className="rule-list">
            {(rules.data ?? []).map((rule) => (
              <article className="rule-card" key={rule.id}>
                <div>
                  <strong>{rule.name}</strong>
                  <span>{rule.survey.name} - priority {rule.priority}</span>
                </div>
                <div className="rule-meta">
                  <span>{rule.hotel?.name ?? "Svi objekti"}</span>
                  <span>{rule.location?.name ?? rule.locationType?.name ?? "Sve lokacije"}</span>
                  <span>{rule.mealPeriod ?? "any period"}</span>
                  <span>{rule.timeWindows?.map((window) => `${window.start}-${window.end}`).join(", ") || "any time"}</span>
                </div>
                <div className="row-actions">
                  <button onClick={() => updateRuleStatus.mutate({ id: rule.id, status: "active" })}>Active</button>
                  <button onClick={() => updateRuleStatus.mutate({ id: rule.id, status: "paused" })}>Pause</button>
                  <button onClick={() => updateRuleStatus.mutate({ id: rule.id, status: "archived" })}>Archive</button>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Preview resolve</h2>
              <p>Simuliraj QR scan za konkretan datum i vrijeme prije stampanja ili promjene pravila.</p>
            </div>
          </div>
          <form className="compact-form" onSubmit={resolvePreview}>
            <h3><FlaskConical size={16} /> Test kontekst</h3>
            <select value={previewQrId} onChange={(event) => setPreviewQrId(event.target.value)} required>
              <option value="">Izaberi QR rutu</option>
              {(qrRoutes.data ?? []).map((route) => <option key={route.id} value={route.id}>{route.shortPath} - {route.label}</option>)}
            </select>
            <input type="datetime-local" value={previewAt} onChange={(event) => setPreviewAt(event.target.value)} />
            <button type="submit"><Route size={16} /> Resolve survey</button>
          </form>
          {previewResult && (
            <div className="preview-result">
              <strong>{previewResult.surveyName ?? "Nema published ankete"}</strong>
              <span>Ruta: {previewResult.shortPath}</span>
              <span>Meal period: {previewResult.mealPeriod}</span>
              <span>Rule: {previewResult.matchedRuleId ?? "fallback"}</span>
              <span>Version: {previewResult.versionNumber ?? "-"}</span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
